import React, { useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

// Use environment variable or fallback to current origin for deployed environments
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const LeadCaptureModal = ({ isOpen, onClose, resource, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Submit lead to backend
      await axios.post(`${API}/leads`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        resource_name: resource.title
      });

      toast.success("Thank you!", {
        description: "Your download will begin shortly.",
        duration: 5000,
      });

      // Reset form
      setFormData({ name: '', email: '', phone: '' });
      
      // Call success callback to trigger download
      onSuccess();
      
      // Close modal after a brief delay to allow user to see the toast
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      toast.error("Error", {
        description: "Failed to process your request. Please try again.",
        duration: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-8 relative animate-in fade-in duration-200">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 transition-colors"
          disabled={loading}
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-[#2C2C2A] mb-2">
            Download Resource
          </h3>
          <p className="text-[#6B6B69]">
            Please provide your details to download: <strong>{resource?.title}</strong>
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-semibold text-[#2C2C2A] mb-2">
              Full Name *
            </label>
            <input
              type="text"
              id="name"
              name="name"
              data-testid="lead-capture-name"
              required
              value={formData.name}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-[#E4E1D8] rounded-xl focus:ring-2 focus:ring-[#0F6E56] focus:border-transparent transition-all text-[#2C2C2A]"
              placeholder="John Smith"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-[#2C2C2A] mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              data-testid="lead-capture-email"
              required
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-[#E4E1D8] rounded-xl focus:ring-2 focus:ring-[#0F6E56] focus:border-transparent transition-all text-[#2C2C2A]"
              placeholder="john@example.com"
            />
          </div>

          <div>
            <label htmlFor="phone" className="block text-sm font-semibold text-[#2C2C2A] mb-2">
              Phone Number *
            </label>
            <input
              type="tel"
              id="phone"
              name="phone"
              data-testid="lead-capture-phone"
              required
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 border border-[#E4E1D8] rounded-xl focus:ring-2 focus:ring-[#0F6E56] focus:border-transparent transition-all text-[#2C2C2A]"
              placeholder="0412 345 678"
            />
          </div>

          <button
            type="submit"
            data-testid="lead-capture-submit"
            disabled={loading}
            className="w-full flex items-center justify-center space-x-2 px-8 py-4 bg-[#0F6E56] text-white rounded-full hover:bg-[#0d5c48] transition-all duration-300 font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                <span>Processing...</span>
              </>
            ) : (
              <span>Download Now</span>
            )}
          </button>

          <p className="text-xs text-[#6B6B69] text-center mt-4">
            By downloading, you agree to receive updates about our services. We respect your privacy.
          </p>
        </form>
      </div>
    </div>
  );
};

export default LeadCaptureModal;
